import { Add, CloudUpload, Delete } from "@mui/icons-material";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import Cookies from "js-cookie";
import { useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useMutation } from "react-query";
import { useNavigate } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import useNotification from "../../hooks/useNotification";
import purchaseService from "../../services/purchase";

interface PurchaseItem {
  qty: number;
  description: string;
  price: number;
  kategori: string;
}

interface PurchaseForm {
  usage_date: Date | null;
  request_date: Date | null;
  input_link: string;
  nama_bank: string;
  no_rek: string;
  detail_toko: string;
  items: PurchaseItem[];
  alamat_pengiriman: string;
  nama_supplier: string;
  jabatan_supplier: string;
  perusahaan_supplier: string;
  nama_penerima: string;
  jabatan_penerima: string;
  perusahaan_penerima: string;
}

export default function PurchaseCreate() {
  const navigate = useNavigate();
  const notification = useNotification();
  const user = JSON.parse(Cookies.get("USER") || "{}");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const [invoiceError, setInvoiceError] = useState<string>("");

  const form = useForm<PurchaseForm>({
    defaultValues: {
      usage_date: null,
      request_date: null,
      input_link: "",
      nama_bank: "",
      no_rek: "",
      detail_toko: "",
      items: [{ qty: 1, description: "", price: 0, kategori: "" }],
      alamat_pengiriman: "",
      nama_supplier: "",
      jabatan_supplier: "",
      perusahaan_supplier: "",
      nama_penerima: "",
      jabatan_penerima: "",
      perusahaan_penerima: "",
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "items",
  });

  const createPurchase = useMutation(purchaseService.createPurchase, {
    onSuccess: () => {
      notification.onOpen({
        message: "Purchase created successfully!",
        type: "success",
        position: "top",
      });
      navigate("/app/purchase");
    },
    onError: () => {
      notification.onOpen({
        message: "Failed to create purchase",
        type: "error",
        position: "top",
      });
      setIsSubmitting(false);
    },
  });

  const onSubmit = (data: PurchaseForm) => {
    if (!invoiceFile) {
      setInvoiceError("Invoice file is required");
      return;
    }
    setInvoiceError("");
    setIsSubmitting(true);
    const payload = {
      usage_date: data.usage_date?.toISOString().split('T')[0],
      request_date: data.request_date?.toISOString().split('T')[0],
      input_link: data.input_link,
      nama_bank: data.nama_bank,
      no_rek: data.no_rek,
      detail_toko: data.detail_toko,
      items: data.items,
      alamat_pengiriman: data.alamat_pengiriman,
      nama_supplier: data.nama_supplier,
      jabatan_supplier: data.jabatan_supplier,
      perusahaan_supplier: data.perusahaan_supplier,
      nama_penerima: data.nama_penerima,
      jabatan_penerima: data.jabatan_penerima,
      perusahaan_penerima: data.perusahaan_penerima,
      created_by: user.id,
      invoice_file: invoiceFile,
    };
    createPurchase.mutate(payload);
  };

  const addItem = () => {
    append({ qty: 1, description: "", price: 0, kategori: "" });
  };

  const removeItem = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  const handleInvoiceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setInvoiceFile(file);
    if (file) setInvoiceError("");
  };

  return (
    <MainLayout>
      <Helmet>
        <title>Create Purchase</title>
      </Helmet>

      <Breadcrumb
        label="Create Purchase"
        breadcrumbs={[
          { label: "Dashboard", href: "/app" },
          { label: "Purchase Management", href: "/app/purchase" },
        ]}
      />

      <ProtectedPermissionPage acceptPermissions={[1, 2]}>
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Purchase Details
          </Typography>
          <Box component="form" onSubmit={form.handleSubmit(onSubmit)} sx={{ mt: 2 }}>
            <Stack spacing={3}>
              <Controller
                name="usage_date"
                control={form.control}
                rules={{ required: "Usage date is required" }}
                render={({ field, fieldState }) => (
                  <DatePicker
                    label="Usage Date *"
                    value={field.value}
                    onChange={field.onChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                )}
              />

              <Controller
                name="request_date"
                control={form.control}
                rules={{ required: "Request date is required" }}
                render={({ field, fieldState }) => (
                  <DatePicker
                    label="Request Date *"
                    value={field.value}
                    onChange={field.onChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                )}
              />

              <Controller
                name="input_link"
                control={form.control}
                rules={{ required: "Input link is required" }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Input Link *"
                    fullWidth
                    variant="outlined"
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                  />
                )}
              />

              <Stack direction={{ xs: "column", sm: "row" }} spacing={3}>
                <Controller
                  name="nama_bank"
                  control={form.control}
                  rules={{ required: "Bank name is required" }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Bank Name *"
                      fullWidth
                      variant="outlined"
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
                <Controller
                  name="no_rek"
                  control={form.control}
                  rules={{ required: "Account number is required" }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label="Account Number *"
                      fullWidth
                      variant="outlined"
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </Stack>

              <Controller
                name="detail_toko"
                control={form.control}
                rules={{ required: "Store detail is required" }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Store Detail *"
                    fullWidth
                    variant="outlined"
                    multiline
                    rows={2}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                  />
                )}
              />

              <Controller
                name="alamat_pengiriman"
                control={form.control}
                rules={{ required: "Alamat pengiriman is required" }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label="Alamat Pengiriman *"
                    fullWidth
                    variant="outlined"
                    multiline
                    rows={2}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                  />
                )}
              />

              <Controller
                name="nama_supplier"
                control={form.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Nama Supplier"
                    fullWidth
                    variant="outlined"
                  />
                )}
              />

              <Controller
                name="jabatan_supplier"
                control={form.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Jabatan Supplier"
                    fullWidth
                    variant="outlined"
                  />
                )}
              />

              <Controller
                name="perusahaan_supplier"
                control={form.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Perusahaan Supplier"
                    fullWidth
                    variant="outlined"
                  />
                )}
              />

              <Controller
                name="nama_penerima"
                control={form.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Nama Penerima"
                    fullWidth
                    variant="outlined"
                  />
                )}
              />

              <Controller
                name="jabatan_penerima"
                control={form.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Jabatan Penerima"
                    fullWidth
                    variant="outlined"
                  />
                )}
              />

              <Controller
                name="perusahaan_penerima"
                control={form.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Perusahaan Penerima"
                    fullWidth
                    variant="outlined"
                  />
                )}
              />

              <Box>
                <Typography variant="h6" gutterBottom>
                  Purchase Items
                </Typography>
                <Stack spacing={2}>
                  {fields.map((field, index) => (
                    <Stack key={field.id} direction="row" spacing={2} alignItems="center">
                      <TextField
                        label="No"
                        size="small"
                        value={index + 1}
                        InputProps={{ readOnly: true }}
                        sx={{ width: 80 }}
                      />
                      <Controller
                        name={`items.${index}.qty`}
                        control={form.control}
                        rules={{ required: true, min: 1 }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            type="number"
                            label="Qty"
                            size="small"
                            onChange={(e) => field.onChange(Number(e.target.value))}
                          />
                        )}
                      />
                      <Controller
                        name={`items.${index}.description`}
                        control={form.control}
                        rules={{ required: "Description is required" }}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            label="Description"
                            size="small"
                            fullWidth
                            error={Boolean(fieldState.error)}
                            helperText={fieldState.error?.message}
                          />
                        )}
                      />
                      <Controller
                        name={`items.${index}.price`}
                        control={form.control}
                        rules={{ required: true }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            type="number"
                            label="Price"
                            size="small"
                            InputProps={{
                              startAdornment: <InputAdornment position="start">Rp</InputAdornment>,
                            }}
                            onChange={(e) => field.onChange(Number(e.target.value))}
                          />
                        )}
                      />
                      <Controller
                        name={`items.${index}.kategori`}
                        control={form.control}
                        rules={{ required: "Kategori is required" }}
                        render={({ field: { ref, ...field }, fieldState }) => (
                          <FormControl size="small" error={Boolean(fieldState.error?.message)} sx={{ minWidth: 180 }}>
                            <InputLabel>Kategori *</InputLabel>
                            <Select
                              {...field}
                              label="Kategori *"
                            >
                              <MenuItem value="Perlengkapan">Perlengkapan</MenuItem>
                              <MenuItem value="Aset">Aset</MenuItem>
                              <MenuItem value="Bahan Baku">Bahan Baku</MenuItem>
                              <MenuItem value="Pengiriman">Pengiriman</MenuItem>
                              <MenuItem value="Maintenance">Maintenance</MenuItem>
                              <MenuItem value="Lainnya">Lainnya</MenuItem>
                            </Select>
                            <FormHelperText>{fieldState.error?.message}</FormHelperText>
                          </FormControl>
                        )}
                      />
                      <IconButton
                        onClick={() => removeItem(index)}
                        disabled={fields.length === 1}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </Stack>
                  ))}
                  <Button
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={addItem}
                    sx={{ alignSelf: "flex-start" }}
                  >
                    Add Item
                  </Button>
                </Stack>
              </Box>

              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Invoice *
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Button
                    variant="outlined"
                    startIcon={<CloudUpload />}
                    onClick={() => fileInputRef.current?.click()}
                    color={invoiceError ? "error" : "primary"}
                  >
                    {invoiceFile ? "Change Invoice" : "Upload Invoice"}
                  </Button>
                  {invoiceFile && (
                    <Typography variant="body2">{invoiceFile.name}</Typography>
                  )}
                </Stack>
                {invoiceError && (
                  <FormHelperText error>{invoiceError}</FormHelperText>
                )}
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleInvoiceChange}
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                />
              </Box>

              <Stack direction="row" spacing={2}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Creating..." : "Create Purchase"}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate("/app/purchase")}
                >
                  Cancel
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Box>
      </ProtectedPermissionPage>
    </MainLayout>
  );
}